import React, { useCallback, useState, memo, useEffect } from 'react';

import type { XCSS } from '@atlaskit/primitives';
import { Box, xcss } from '@atlaskit/primitives';

import { usePubSubEvent } from '@confluence/pubsub-client';
import {
	useIsDataClassificationEnabledForOrg,
	DataClassificationHoverButton,
} from '@confluence/data-classification';

import { CoverPictureAndButton } from './CoverPictureAndButton';
import { EmojiButton } from './EmojiButton';
import { ContentStateHoverButton } from './ContentStateHoverButton';
import type { AreaAboveTitleProps } from './titleareaContentComponentsTypes';

const areaAboveTitleBoxStyles = xcss({
	position: 'relative',
	bottom: 'space.100',
	width: '100%',
	display: 'flex',
	minHeight: 'space.300',
});

const emojiButtonWithCoverPictureBoxStyles = xcss({
	position: 'absolute',
	top: '155px',
});

const coverPictureAndButtonWithCoverPictureBoxStyles = xcss({
	width: '100%',
	marginBottom: 'space.500',
});

const coverPictureAndButtonHeightWithoutCustomSitesBoxStyles = xcss({
	height: '140px',
});

const contentStateButtonWithCoverPictureBoxStyles = xcss({
	position: 'absolute',
	top: '155px',
});

const dataClassificationButtonWithCoverPictureBoxStyles = (dataClassificationPosition) =>
	xcss({
		position: 'absolute',
		top: '155px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		left: dataClassificationPosition,
	});

export const AreaAboveTitle = memo(
	({
		contentId,
		userOffline,
		isUnpublishedDraft,
		isTitleDisabled,
		isTitleHovered,
		isCoverPictureModalOpen,
		hasCoverPicture,
		isUploadingPicture,
		isAddHeaderImageButtonFocused,
		setSelectedEmoji,
		onEmojiPickerLoaded,
		onUpdateStartEmoji,
		onUpdateEndEmoji,
		isUpdatingEmoji,
		isEmojiButtonFocused,
		setIsTitleHovered,
		contentType,
		isPublishingContent,
		hasContentState,
		contentStateState,
		coverPictureState,
		contentClassificationMetadata,
		hasClassification,
		isReadOnly,
		spaceKey,
	}: AreaAboveTitleProps) => {
		const { isEnabled: isDataClassificationEnabledForOrg } = useIsDataClassificationEnabledForOrg();

		const [isContentStateButtonFocused, setIsContentStateButtonFocused] = useState(false);
		const [isContentStateMenuOpen, setIsContentStateMenuOpen] = useState(false);

		const [isClassificationButtonFocused, setIsClassificationButtonFocused] = useState(false);
		const [isClassificationMenuOpen, setIsClassificationMenuOpen] = useState(false);

		const isCoverPictureAreaRendered = isUploadingPicture || hasCoverPicture;

		// Tracks if any of the hover buttons are focused (means user tabbed to a hover button)
		const [isHoverButtonFocused, _setIsHoverButtonFocused] = useState<boolean>(
			isAddHeaderImageButtonFocused ||
				isEmojiButtonFocused ||
				isContentStateButtonFocused ||
				isClassificationButtonFocused,
		);
		const shouldShowHoverButton =
			isTitleHovered || isHoverButtonFocused || isContentStateMenuOpen || isClassificationMenuOpen;

		const { shouldDisplayClassification, hasNoClassification } = contentClassificationMetadata;

		/* The state is used to track width of emoji button since the width of the button differs with language.
  Due to the absolute positioning, the emoji button can overlap the content state button if a page does not have a content state.
  This width is passed to the content state button so it can space itself accordingly.
   */
		const [emojiButtonWidth, setEmojiButtonWidth] = useState<number>(0);
		const [dataClassificationPosition, setDataClassificationPosition] = useState<string>('');
		useEffect(() => {
			return () => {
				// Whenever we leave the editor, we need to clean up and reset the content state container values on unmount,
				// otherwise when the user opens editor, previous values are incorrectly persisted (could also do this in breadcrumbs but chose here)
				void contentStateState.resetToDefaults();
				void coverPictureState.resetToDefaults();
			};
		}, [contentStateState, coverPictureState]);

		const emojiButton = (
			<EmojiButton
				contentId={contentId}
				userOffline={userOffline}
				isUnpublishedDraft={isUnpublishedDraft}
				isTitleDisabled={isTitleDisabled}
				shouldShowHoverButton={shouldShowHoverButton}
				isCoverPictureModalOpen={isCoverPictureModalOpen}
				setSelectedEmoji={setSelectedEmoji}
				onEmojiPickerLoaded={onEmojiPickerLoaded}
				onUpdateStartEmoji={onUpdateStartEmoji}
				onUpdateEndEmoji={onUpdateEndEmoji}
				isUpdatingEmoji={isUpdatingEmoji}
				setIsTitleHovered={setIsTitleHovered}
			/>
		);

		const coverPicAndButton = (
			<CoverPictureAndButton
				contentId={contentId}
				contentType={contentType}
				isPublishingContent={isPublishingContent}
				userOffline={userOffline}
				hasCoverPicture={hasCoverPicture}
				isUploadingPicture={isUploadingPicture}
				shouldShowHoverButton={shouldShowHoverButton}
				hasContentState={hasContentState}
				isUnpublishedDraft={isUnpublishedDraft}
				isReadOnly={isReadOnly}
			/>
		);

		const contentStateHoverButton = (
			<ContentStateHoverButton
				contentId={contentId}
				shouldShowHoverButton={shouldShowHoverButton}
				hasContentState={hasContentState}
				contentType={contentType}
				userOffline={userOffline}
				setIsContentStateButtonFocused={setIsContentStateButtonFocused}
				setIsContentStateMenuOpen={setIsContentStateMenuOpen}
			/>
		);

		const dataClassificationHoverButton = !shouldDisplayClassification &&
			isDataClassificationEnabledForOrg &&
			hasNoClassification && (
				<DataClassificationHoverButton
					contentId={contentId}
					shouldShowHoverButton={shouldShowHoverButton}
					userOffline={userOffline}
					setIsClassificationButtonFocused={setIsClassificationButtonFocused}
					setIsClassificationMenuOpen={setIsClassificationMenuOpen}
					contentClassificationMetadata={contentClassificationMetadata}
					hasClassification={hasClassification}
					isCustomSitesPageTitleFFOn={false}
					spaceKey={spaceKey}
				/>
			);

		// https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
		const emojiButtonMeasuredRef = useCallback(
			(node: any) => {
				if (node !== null) {
					const width = node.offsetWidth;
					setEmojiButtonWidth(width);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[emojiButton],
		);

		// https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
		const contentStateHoverButtonRef = useCallback(
			(node: any) => {
				if (node !== null) {
					const width = node.offsetWidth;
					// Sets data classification to the right of content state button
					setDataClassificationPosition(`${emojiButtonWidth + width}px`);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[contentStateHoverButton],
		);

		// Putting pubsub subscription in this file since putting it in the contentState component won't work due to re-renders
		// from unmounting and mounting from the breadcrumbs component and contentStateHoverButton component
		const onContentStateUpdated = useCallback(
			(_eventName: any, pubSubData: any) => {
				const newContentState = JSON.parse(
					pubSubData.contentPropertyUpdateRequestBody.value,
				).contentState;
				void contentStateState.setContentState(newContentState);
			},
			[contentStateState],
		);

		const onContentStateDeleted = useCallback(
			(_eventName: any, _pubSubData: any) => {
				void contentStateState.setContentState(null);
			},
			[contentStateState],
		);

		usePubSubEvent({
			contentId,
			contentType: contentType || 'page',
			eventName: 'updated:contentState',
			onEvent: onContentStateUpdated,
		});

		usePubSubEvent({
			contentId,
			contentType: contentType || 'page',
			eventName: 'deleted:contentState',
			onEvent: onContentStateDeleted,
		});

		const emojiButtonXcss = isCoverPictureAreaRendered
			? [emojiButtonWithCoverPictureBoxStyles]
			: [];

		const coverPictureAndButtonXcss: Array<XCSS> = [];
		if (isCoverPictureAreaRendered) {
			coverPictureAndButtonXcss.push(coverPictureAndButtonWithCoverPictureBoxStyles);
			coverPictureAndButtonXcss.push(coverPictureAndButtonHeightWithoutCustomSitesBoxStyles);
		}

		const contentStateButtonXcss = isCoverPictureAreaRendered
			? contentStateButtonWithCoverPictureBoxStyles
			: undefined;

		const dataClassificationButtonXcss = isCoverPictureAreaRendered
			? dataClassificationButtonWithCoverPictureBoxStyles(dataClassificationPosition)
			: undefined;

		const contentStateButtonInlineCss = isCoverPictureAreaRendered
			? // This is a dynamic value so tokens cannot be used here
				// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
				{ left: `${emojiButtonWidth}px` }
			: {};

		return (
			<Box xcss={areaAboveTitleBoxStyles}>
				{!isReadOnly && (
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
					<Box ref={emojiButtonMeasuredRef} xcss={emojiButtonXcss} testId="emoji-button-wrapper">
						{emojiButton}
					</Box>
				)}
				{/* eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage */}
				<Box xcss={coverPictureAndButtonXcss} testId="cover-pic-and-button-wrapper">
					{coverPicAndButton}
				</Box>
				{!isReadOnly && (
					<Box
						ref={contentStateHoverButtonRef}
						// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
						xcss={contentStateButtonXcss}
						testId="content-state-button-wrapper"
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={contentStateButtonInlineCss}
					>
						{contentStateHoverButton}
					</Box>
				)}
				<Box
					// originally used the same css as contentStateButtonXcss, which means that this button rendered on top of content state button
					// this will be fixed in a follow up by cc-dlp
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
					xcss={dataClassificationButtonXcss}
					testId="data-classification-button-wrapper"
				>
					{dataClassificationHoverButton}
				</Box>
			</Box>
		);
	},
);
